// KiCad projects (ERP-PLAN §5.11): HHL Ops makes a board's manufacturing files from its KiCad project
// (server/src/kicad.ts runs KiCad 10; deploy/kicad/worker.py). Here, shared and pure: which dropped files are the
// project, what KiCad's Gerber job file says about the board (layers, thickness, size, copper, finish, colours, the
// smallest track, gap and hole), and that as the spec's "Board" section (English; 中文 when the spec has Chinese text),
// which a newer project replaces and nothing else touches.

/** A file of a KiCad project HHL Ops sends to KiCad: the project, board, schematics, design rules and library tables. */
export const KICAD_PROJECT_FILE = /\.(kicad_pro|kicad_pcb|kicad_sch|kicad_dru|kicad_wks)$|^(sym|fp)-lib-table$/i;
/** KiCad's own backups and autosaves, never part of a project sent. */
const KICAD_JUNK = /^(_autosave-|~)|\.(lck|bak)$|-bak$/i;

const base = (path: string) => path.split(/[\\/]/).pop() ?? path;
const dir = (path: string) => path.split(/[\\/]/).slice(0, -1).join("/");

/**
 * The KiCad project among these paths: its board's name and the paths to send (everything of the project in the
 * board's folder and below, so hierarchical sheets in subfolders come along). Null when there's no board, or more
 * than one (one board's project at a time).
 */
export function kicadProject(paths: readonly string[]): { board: string; files: string[]; folder: string } | null {
  const real = paths.filter((p) => !KICAD_JUNK.test(base(p)) && !p.split(/[\\/]/).some((f) => /-backups$/i.test(f)));
  const boards = real.filter((p) => /\.kicad_pcb$/i.test(p));
  if (boards.length !== 1) return null;
  const folder = dir(boards[0]);
  const inside = (p: string) => !folder || p.startsWith(`${folder}/`) || p.startsWith(`${folder}\\`);
  return { board: base(boards[0]).replace(/\.kicad_pcb$/i, ""), files: real.filter((p) => inside(p) && KICAD_PROJECT_FILE.test(base(p))), folder };
}

/** A project file's name in the zip sent: its path under the board's folder (sheets in subfolders keep theirs). */
export const projectPath = (path: string, folder: string) => (folder ? path.slice(folder.length + 1) : path).replace(/\\/g, "/");

export type Board = {
  /** KiCad's project name ("UGC_Main_R5"). */
  name: string;
  layers: number | null;
  /** In mm. */
  thickness: number | null;
  size: { x: number; y: number } | null;
  /** Ounces, to the half (0.035 mm is 1 oz); inner null on a 2-layer board. */
  copper: { outer: number | null; inner: number | null };
  /** KiCad's words ("ENIG", "HAL lead-free"); null when it says None (not set). */
  finish: string | null;
  /** Solder mask and silkscreen colours, top then bottom (each once); [] when not set. */
  mask: string[];
  silk: string[];
  /** The smallest track, gap and drilled hole, in mm. */
  minTrack: number | null;
  minGap: number | null;
  minHole: number | null;
  /** Impedance control, castellated pads, edge plating, an edge connector: only those the board says it has. */
  features: string[];
};

type Job = {
  GeneralSpecs?: {
    ProjectId?: { Name?: string };
    Size?: { X?: number; Y?: number };
    LayerNumber?: number;
    BoardThickness?: number;
    Finish?: string;
    ImpedanceControlled?: string;
    Castellated?: string;
    EdgePlating?: string;
    EdgeConnector?: string;
  };
  MaterialStackup?: { Type?: string; Name?: string; Thickness?: number; Color?: string }[];
  DesignRules?: { Layers?: string; MinLineWidth?: number; TrackToTrack?: number; PadToPad?: number; PadToTrack?: number }[];
};

const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
const yes = (v: unknown) => typeof v === "string" && /^(yes|true|bevelled)$/i.test(v);
const oz = (mm: number | null | undefined) => (mm ? Math.max(0.5, Math.round((mm / 0.035) * 2) / 2) : null);
const unique = (list: (string | undefined)[]) => [...new Set(list.filter((s): s is string => !!s && !/^not specified$/i.test(s)))];

/** The smallest drill among Excellon files' tool tables (KiCad's .drl), in mm. */
export function smallestHole(drills: readonly string[]): number | null {
  let min: number | null = null;
  for (const text of drills) {
    const inch = /^INCH\b/m.test(text) && !/^METRIC\b/m.test(text);
    for (const m of text.matchAll(/^T\d+C([\d.]+)/gm)) {
      const d = Number(m[1]) * (inch ? 25.4 : 1);
      if (d > 0 && (min === null || d < min)) min = d;
    }
  }
  return min === null ? null : Math.round(min * 1000) / 1000;
}

/** What KiCad's Gerber job file (.gbrjob, JSON) and drill files say about the board. */
export function boardFromJob(jobText: string, drills: readonly string[] = [], name = ""): Board {
  let job: Job = {};
  try {
    job = JSON.parse(jobText) as Job;
  } catch {}
  const g = job.GeneralSpecs ?? {};
  const stack = job.MaterialStackup ?? [];
  const copper = stack.filter((m) => m.Type === "Copper");
  const outer = copper.filter((m) => /^(F|B)\.Cu$/.test(m.Name ?? ""));
  const inner = copper.filter((m) => !/^(F|B)\.Cu$/.test(m.Name ?? ""));
  const rules = job.DesignRules ?? [];
  const smallest = (vals: (number | undefined)[]) => {
    const list = vals.map(num).filter((v): v is number => v !== null);
    return list.length ? Math.min(...list) : null;
  };
  const finish = g.Finish && !/^none$/i.test(g.Finish) ? g.Finish : null;
  return {
    name: g.ProjectId?.Name || name,
    layers: num(g.LayerNumber),
    thickness: num(g.BoardThickness),
    size: num(g.Size?.X) && num(g.Size?.Y) ? { x: g.Size?.X as number, y: g.Size?.Y as number } : null,
    copper: { outer: oz(smallest(outer.map((m) => m.Thickness))), inner: oz(smallest(inner.map((m) => m.Thickness))) },
    finish,
    mask: unique(stack.filter((m) => m.Type === "SolderMask").map((m) => m.Color)),
    silk: unique(stack.filter((m) => m.Type === "Legend").map((m) => m.Color)),
    minTrack: smallest(rules.map((r) => r.MinLineWidth)),
    minGap: smallest(rules.flatMap((r) => [r.TrackToTrack, r.PadToTrack, r.PadToPad])),
    minHole: smallestHole(drills),
    features: [
      yes(g.ImpedanceControlled) && "Impedance controlled",
      yes(g.Castellated) && "Castellated pads",
      yes(g.EdgePlating) && "Edge plating",
      yes(g.EdgeConnector) && `Edge connector${/bevelled/i.test(g.EdgeConnector ?? "") ? " (bevelled)" : ""}`,
    ].filter((f): f is string => !!f),
  };
}

/** What a manufacturer would ask about before making the board. */
export function boardWarnings(b: Board): string[] {
  return [
    !b.finish && "Copper finish isn't set in KiCad (Board Setup → Physical Stackup → Copper finish)",
    !b.size && "No board outline (Edge.Cuts) for KiCad to measure",
    !b.layers && "KiCad didn't say how many copper layers the board has",
  ].filter((w): w is string => !!w);
}

const mm = (n: number) => `${Number(n.toFixed(3))} mm`;
const ozText = (n: number) => `${n} oz`;

// KiCad's copper finishes in Chinese, as Chinese PCB fabs write them.
const FINISH_ZH: Record<string, string> = {
  enig: "沉金",
  enepig: "镍钯金",
  "hal snpb": "有铅喷锡",
  "hal lead-free": "无铅喷锡",
  "hard gold": "镀硬金",
  "immersion tin": "沉锡",
  "immersion silver": "沉银",
  "immersion nickel": "沉镍",
  "immersion gold": "沉金",
  osp: "OSP",
  ht_osp: "高温 OSP",
};
const COLOUR_ZH: Record<string, string> = {
  green: "绿色",
  red: "红色",
  blue: "蓝色",
  purple: "紫色",
  black: "黑色",
  "matte black": "哑黑",
  white: "白色",
  yellow: "黄色",
  "matte green": "哑绿",
};

// The layer order (Mitch: "a 4 layer has its ground layer on the wrong layer on product"). Each Gerber KiCad makes says
// what it is inside (Gerber X2's FileFunction: "Copper,L2,Inr", "Glue,Top"), and its job file lists every file with it:
// the standard a fab's CAM reads. Not every fab reads it, so it's also written out, top to bottom, in the Board section
// and as a text file in the Gerbers zip, with the names the board gives its layers ("GND") and which are planes.

/** A Gerber file as KiCad's job file lists it, with the board's layer it's from. */
export type GerberLayer = {
  file: string;
  /** Gerber X2's file function: "Copper", "SolderMask", "SolderPaste", "Legend", "Glue", "Profile", "Other"… */
  fn: string;
  /** A copper layer's position from the top (L1…); null for the rest. */
  copper: number | null;
  side: "top" | "inner" | "bottom" | null;
  /** Negative polarity (a solder mask: the file draws the openings). */
  negative: boolean;
  /** KiCad's layer ("In1.Cu"; the file's name when the board doesn't list it). */
  kicad: string;
  /** The name the board gives a copper layer ("GND"); "" when none. */
  name: string;
  /** A plane (KiCad's "power" layer type). */
  plane: boolean;
};

/** The board's layers as its .kicad_pcb lists them: KiCad's name → its type and the name it was given ("" when none). */
export function boardLayers(pcbText: string): Map<string, { type: string; name: string }> {
  const out = new Map<string, { type: string; name: string }>();
  for (const m of pcbText.matchAll(/^\s*\(\d+\s+"([^"]+)"\s+(signal|power|mixed|jumper|user)(?:\s+"([^"]*)")?\)\s*$/gm))
    out.set(m[1], { type: m[2], name: m[3] ?? "" });
  return out;
}

/** Top to bottom as the board is made: the top's silkscreen, paste, glue and mask, the copper, the bottom's in reverse, then the rest. */
const OUTSIDE_IN = ["Legend", "SolderPaste", "Glue", "SolderMask"];
const rank = (l: GerberLayer) =>
  l.copper !== null
    ? 100 + l.copper
    : l.side === "top"
      ? OUTSIDE_IN.indexOf(l.fn) + 10
      : l.side === "bottom"
        ? 300 - OUTSIDE_IN.indexOf(l.fn)
        : l.fn === "Profile"
          ? 400
          : 500;

/** The Gerber files in KiCad's job file, top to bottom, each with its board layer (`pcbText`: the .kicad_pcb, for names). */
export function gerberLayers(jobText: string, pcbText = "", board = ""): GerberLayer[] {
  let job: { FilesAttributes?: { Path?: string; FileFunction?: string; FilePolarity?: string }[] } = {};
  try {
    job = JSON.parse(jobText);
  } catch {}
  const named = boardLayers(pcbText);
  const byName = new Map([...named].flatMap(([k, v]) => [[k.toLowerCase(), k] as const, ...(v.name ? [[v.name.toLowerCase(), k] as const] : [])]));
  return (job.FilesAttributes ?? [])
    .flatMap((f): GerberLayer[] => {
      if (!f.Path || !f.FileFunction) return [];
      const parts = f.FileFunction.split(",");
      const fn = parts[0];
      const side = parts.includes("Top") ? "top" : parts.includes("Bot") ? "bottom" : parts.includes("Inr") ? "inner" : null;
      // "UGC_Main_R5-In1_Cu.gbr" → "In1.Cu"; a layer the board renamed is found by its name too.
      const stem = f.Path.replace(/\.[^.]+$/, "");
      const suffix = (board && stem.startsWith(`${board}-`) ? stem.slice(board.length + 1) : (stem.split("-").pop() ?? stem)).replace("_", ".");
      const kicad = byName.get(suffix.toLowerCase()) ?? suffix;
      const own = named.get(kicad);
      return [
        {
          file: f.Path,
          fn,
          copper: fn === "Copper" ? Number(parts[1]?.replace(/^L/, "")) || null : null,
          side,
          negative: f.FilePolarity === "Negative",
          kicad,
          name: fn === "Copper" && own?.name && own.name !== kicad ? own.name : "",
          plane: own?.type === "power",
        },
      ];
    })
    .sort((a, b) => rank(a) - rank(b));
}

const FN_WORDS: Record<string, [string, string]> = {
  SolderMask: ["Solder mask", "阻焊"],
  SolderPaste: ["Paste (stencil)", "钢网"],
  Legend: ["Silkscreen", "丝印"],
  Glue: ["Adhesive (glue)", "红胶"],
  Profile: ["Board outline", "外形"],
  Other: ["Other (a KiCad user layer)", "其他（KiCad 用户层）"],
};
/** A layer in words: "Inner copper, plane “GND”", "Top solder mask (negative)"; and where it sits: "L2", "Top". */
function layerWords(l: GerberLayer, zh: boolean): { at: string; what: string } {
  const sideEn = l.side === "top" ? "Top" : l.side === "bottom" ? "Bottom" : l.side === "inner" ? "Inner" : "";
  const sideZh = l.side === "top" ? "顶层" : l.side === "bottom" ? "底层" : l.side === "inner" ? "内层" : "";
  if (l.copper !== null) {
    const extra = [l.plane && (zh ? "平面层" : "plane"), l.name && `“${l.name}”`].filter(Boolean).join(" ");
    return { at: `L${l.copper}`, what: zh ? `${sideZh}铜${extra ? `，${extra}` : ""}` : `${sideEn} copper${extra ? `, ${extra}` : ""}` };
  }
  const [en, cn] = FN_WORDS[l.fn] ?? [l.fn, l.fn];
  const neg = l.negative ? (zh ? "（负片）" : " (negative)") : "";
  return {
    at: zh ? (l.side === "top" ? "顶" : l.side === "bottom" ? "底" : "") : sideEn,
    what: zh ? `${sideZh}${cn}${neg}` : `${sideEn ? `${sideEn} ${en.toLowerCase()}` : en}${neg}`,
  };
}

/** The layer order as the Board section's table. */
function layerTable(layers: GerberLayer[], zh: boolean): string[] {
  if (!layers.length) return [];
  const cell = (s: string) => s.replace(/\|/g, "\\|");
  return [
    zh ? "### 层序（从顶层到底层）" : "### Layer order, top to bottom",
    "",
    zh ? "| 层 | 内容 | KiCad 层名 | 文件 |" : "| Layer | What it is | KiCad layer | File |",
    "|---|---|---|---|",
    ...layers.map((l) => {
      const w = layerWords(l, zh);
      return `| ${w.at || "—"} | ${cell(w.what)} | ${cell(l.kicad)} | ${cell(l.file)} |`;
    }),
    "",
  ];
}

/** The layer order as a text file for the Gerbers zip, English and Chinese, so the fab sees it with the files. */
export function layerOrderText(layers: GerberLayer[], board: string, jobFile = ""): string {
  const rows = layers.map((l) => ({ en: layerWords(l, false), zh: layerWords(l, true).what, file: l.file }));
  // Columns padded to line up (by characters: the Chinese ones are as wide in a fixed-width viewer that shows them double).
  const width = (texts: string[]) => Math.max(2, ...texts.map((t) => [...t].length)) + 2;
  const pad = (s: string, n: number) => s + " ".repeat(n - [...s].length);
  const at = width(rows.map((r) => r.en.at || "-"));
  const en = width(rows.map((r) => r.en.what));
  const zh = width(rows.map((r) => r.zh));
  return [
    `${board}: layer order, top to bottom / 层序（从顶层到底层）`,
    "",
    ...rows.map((r) => `${pad(r.en.at || "-", at)}${pad(r.en.what, en)}${pad(r.zh, zh)}${r.file}`),
    "",
    `Made by HHL Ops from the KiCad project. Each Gerber file also says its layer inside (Gerber X2)${jobFile ? `, and ${jobFile} lists them all` : ""}.`,
    `由 HHL Ops 根据 KiCad 工程文件生成。每个 Gerber 文件内部也标明了自己的层（Gerber X2）${jobFile ? `，${jobFile} 列出了全部文件` : ""}。`,
    "",
  ].join("\r\n");
}

/** The section's heading, by language: what a newer project's section replaces. */
export const BOARD_HEADING = { en: "## Board", zh: "## 板子参数" } as const;

/** The spec's "Board" section for this board: a table a manufacturer reads, its layer order, then where it came from. */
export function boardMarkdown(b: Board, made: { kicad: string; day: string }, lang: "en" | "zh" = "en", layers: GerberLayer[] = []): string {
  const zh = lang === "zh";
  const unset = zh ? "未在 KiCad 中设置" : "Not set in KiCad";
  const colours = (list: string[]) => (list.length ? list.map((c) => (zh ? (COLOUR_ZH[c.toLowerCase()] ?? c) : c)).join(" / ") : unset);
  const copper = [
    b.copper.outer !== null && (zh ? `外层 ${ozText(b.copper.outer)}` : `${ozText(b.copper.outer)} outer`),
    b.copper.inner !== null && (zh ? `内层 ${ozText(b.copper.inner)}` : `${ozText(b.copper.inner)} inner`),
  ].filter(Boolean);
  const rows: [string, string, string][] = [
    ["Layers", "层数", b.layers !== null ? String(b.layers) : unset],
    ["Thickness", "板厚", b.thickness !== null ? mm(b.thickness) : unset],
    ["Size", "尺寸", b.size ? `${Number(b.size.x.toFixed(2))} × ${Number(b.size.y.toFixed(2))} mm` : unset],
    ["Copper", "铜厚", copper.length ? copper.join(zh ? "，" : ", ") : unset],
    ["Copper finish", "表面处理", b.finish ? (zh ? (FINISH_ZH[b.finish.toLowerCase()] ?? b.finish) : b.finish) : unset],
    ["Solder mask", "阻焊颜色", colours(b.mask)],
    ["Silkscreen", "丝印颜色", colours(b.silk)],
    [
      "Smallest track / gap",
      "最小线宽 / 线距",
      b.minTrack !== null || b.minGap !== null ? `${b.minTrack !== null ? mm(b.minTrack) : "?"} / ${b.minGap !== null ? mm(b.minGap) : "?"}` : unset,
    ],
    ["Smallest hole", "最小孔径", b.minHole !== null ? mm(b.minHole) : unset],
  ];
  const FEATURE_ZH: Record<string, string> = {
    "Impedance controlled": "阻抗控制",
    "Castellated pads": "半孔",
    "Edge plating": "板边包金",
    "Edge connector": "金手指",
    "Edge connector (bevelled)": "金手指（斜边）",
  };
  if (b.features.length) rows.push(["Also", "其他", b.features.map((f) => (zh ? (FEATURE_ZH[f] ?? f) : f)).join(zh ? "，" : ", ")]);
  const cell = (s: string) => s.replace(/\|/g, "\\|");
  return [
    zh ? BOARD_HEADING.zh : BOARD_HEADING.en,
    "",
    `| ${zh ? "板子" : "Board"} | ${cell(b.name || "?")} |`,
    "|---|---|",
    ...rows.map(([en, cn, value]) => `| ${zh ? cn : en} | ${cell(value)} |`),
    "",
    ...layerTable(layers, zh),
    zh
      ? `以上参数由 HHL Ops 于 ${made.day} 从 KiCad 工程文件读取（${made.kicad}）。`
      : `Read from the KiCad project by HHL Ops on ${made.day} (${made.kicad}). A newer project replaces this section.`,
    "",
  ].join("\n");
}

/**
 * The text with its Board section (`heading`) replaced by `section`, or added: after the Specifications section
 * (`after`) when there is one, else at the top. A section runs to the next heading of its level or above.
 */
export function withSection(md: string, heading: string, section: string, after = "## Specifications"): string {
  const lines = md.split("\n");
  const level = (l: string) => /^(#{1,6})\s/.exec(l)?.[1].length ?? 0;
  const end = (from: number) => {
    const at = level(lines[from]);
    for (let i = from + 1; i < lines.length; i++) if (level(lines[i]) && level(lines[i]) <= at) return i;
    return lines.length;
  };
  const body = section.replace(/\n+$/, "").split("\n");
  const at = lines.findIndex((l) => l.trim() === heading);
  if (at >= 0) return [...lines.slice(0, at), ...body, "", ...lines.slice(end(at))].join("\n").replace(/\n{3,}/g, "\n\n");
  const spec = lines.findIndex((l) => l.trim() === after);
  const put = spec >= 0 ? end(spec) : 0;
  return [...lines.slice(0, put), ...(put > 0 && lines[put - 1]?.trim() ? [""] : []), ...body, "", ...lines.slice(put)].join("\n").replace(/\n{3,}/g, "\n\n");
}
